<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { storeToRefs } from 'pinia';
import { SkipByItem } from '../../types';
import {
  SKIP_BY_DEFAULT,
  SKIP_BY_HEADINGS,
  SKIP_BY_PAGES,
  SKIP_BY_TIME,
} from '../../composables/use-navigation-controls';
import useLocale from '../../composables/useLocale';
import useNavigationStore from '../../stores/navigation';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faForwardFast,
  faH1,
  faH2,
  faH3,
  faH4,
  faH5,
  faH6,
  faArrowRotateRight,
  faArrowRotateLeft,
  faFileExport,
  faArrowRotateRight15,
  faArrowRotateRight30,
} from '@fortawesome/pro-solid-svg-icons';
import { faSquare } from '@fortawesome/pro-regular-svg-icons';
library.add([
  faForwardFast,
  faH1,
  faH2,
  faH3,
  faH4,
  faH5,
  faH6,
  faSquare,
  faArrowRotateRight,
  faArrowRotateLeft,
  faFileExport,
  faArrowRotateRight15,
  faArrowRotateRight30,
]);

const modelValue = defineModel<string>({ required: true });

const { t } = useLocale();
const { smAndDown } = useDisplay();
const { navigationLevels, pages } = storeToRefs(useNavigationStore());

const skipByItems = computed<SkipByItem[]>(() => {
  const items: SkipByItem[] = [
    {
      title: 'skip_by_default',
      desc: `book_player.message_skip_by_default`,
      value: SKIP_BY_DEFAULT,
      icon: 'fa-solid fa-forward-fast',
    },
  ];

  // Map heading levels
  Object.keys(navigationLevels.value).forEach((levelKey) => {
    const levelNum = parseInt(levelKey, 10);
    items.push({
      title: `skip_by_heading`,
      desc: `book_player.message_skip_by_heading`,
      level: `${levelNum + 1}`,
      value: `${SKIP_BY_HEADINGS}_${levelNum + 1}`,
      icon: `fas fa-h${levelNum + 1}`,
    });
  });

  // Check if pages exist
  if (pages.value.length > 0) {
    items.push({
      title: 'skip_by_pages',
      desc: `book_player.message_skip_by_page`,
      value: SKIP_BY_PAGES,
      icon: 'fa-solid fa-file-export',
    });
  }

  // Time-based skipping
  items.push(
    {
      title: `skip_by_15_seconds`,
      desc: `book_player.message_skip_by_15_seconds`,
      value: `${SKIP_BY_TIME}_15`,
      icon: 'fa-solid fa-arrow-rotate-right-15',
    },
    {
      title: `skip_by_30_seconds`,
      desc: `book_player.message_skip_by_30_seconds`,
      value: `${SKIP_BY_TIME}_30`,
      icon: 'fa-solid fa-arrow-rotate-right-30',
    },
  );

  return items;
});
</script>

<template>
  <v-list-subheader class="text-surface">
    {{ t('book_player.label_navigation_levels') }}
  </v-list-subheader>

  <v-list-item density="compact" data-testid="bp-setting-skip-by" :aria-label="t('skip_by')">
    <v-select
      v-model="modelValue"
      :items="skipByItems"
      item-title="title"
      item-value="value"
      :label="t('book_player.label_select_navigation_level')"
      color="primary"
      :no-data-text="t('book_player.label_no_navigation_levels')"
      :aria-label="t('book_player.label_select_navigation_level')"
      :single-line="true"
      :hide-details="true"
    >
      <template #item="{ item, props }">
        <v-list-item
          v-bind="{ ...props, title: undefined, desc: undefined, icon: undefined }"
          role="option"
          :aria-selected="props.selected"
          :aria-label="
            item.raw?.level
              ? t(item.raw?.desc ?? item.title, { level: item.raw.level })
              : t(item.raw?.desc ?? item.title)
          "
        >
          <template #prepend>
            <FontAwesomeIcon
              :size="!smAndDown ? 'lg' : undefined"
              class="pr-7"
              :icon="item.raw.icon ? item.raw.icon : 'fa-solid fa-square'"
            />
          </template>
          <v-list-item-title>
            {{ item.raw?.level ? t(item.title, { level: item.raw.level }) : t(item.title) }}
          </v-list-item-title>
        </v-list-item>
      </template>
      <template #selection="{ item }">
        <span
          class="d-flex align-center"
          :aria-label="
            item.raw?.level
              ? t(item.raw?.desc ?? item.title, { level: item.raw.level })
              : t(item.raw?.desc ?? item.title)
          "
        >
          <FontAwesomeIcon
            :size="!smAndDown ? 'lg' : undefined"
            :icon="item.raw.icon ? item.raw.icon : 'fa-solid fa-square'"
          />
          <span class="v-icon-spacer" />
          <span>
            {{ item.raw?.level ? t(item.title, { level: item.raw.level }) : t(item.title) }}
          </span>
        </span>
      </template>
    </v-select>
  </v-list-item>
</template>

<style scoped lang="scss">
:deep(.v-icon) {
  opacity: 1 !important;
}
.v-icon-spacer {
  width: 28px;
}
</style>
